// src/pages/supervisor/StudentFeedbackAnalytics.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    LinearProgress,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Rating,
    Tab,
    Tabs,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Star,
    StarBorder,
    TrendingUp,
    TrendingDown,
    CheckCircle,
    Warning,
    Schedule,
    Person,
    Email,
    Message,
    Feedback,
    Analytics,
    BarChart,
    PieChart,
    Timeline,
    ThumbUp,
    ThumbDown,
    RateReview,
    Send,
    Close,
    Info
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIStudentFeedbackList,
    APIStudentFeedbackAnalytics,
    APIStudentFeedbackSubmitResponse
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import SupervisorBar from '../../components/SupervisorBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const StudentFeedbackAnalytics = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [feedbackList, setFeedbackList] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [openResponseDialog, setOpenResponseDialog] = useState(false);
    const [responseForm, setResponseForm] = useState({ response: '', action_plan: '' });
    const [actionLoading, setActionLoading] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const supervisorId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await Promise.all([
                fetchFeedbackList(),
                fetchAnalytics()
            ]);
        } catch (err) {
            setError("Failed to load feedback data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchFeedbackList = async () => {
        const result = await apiService.listById(APIStudentFeedbackList, supervisorId);
        if (result.success && isMounted.current) {
            setFeedbackList(Array.isArray(result.data) ? result.data : []);
        }
    };

    const fetchAnalytics = async () => {
        const result = await apiService.listById(APIStudentFeedbackAnalytics, supervisorId);
        if (result.success && result.data && isMounted.current) {
            setAnalytics(result.data);
        }
    };

    const handleOpenResponseDialog = (feedback) => {
        setSelectedFeedback(feedback);
        setResponseForm({ response: '', action_plan: '' });
        setOpenResponseDialog(true);
    };

    const handleCloseResponseDialog = () => {
        setOpenResponseDialog(false);
        setSelectedFeedback(null);
        setResponseForm({ response: '', action_plan: '' });
    };

    const handleSubmitResponse = async () => {
        if (!responseForm.response) {
            showToast("Error", "Please enter a response", "error");
            return;
        }
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('feedback_id', selectedFeedback.id);
            formData.append('supervisor_response', responseForm.response);
            if (responseForm.action_plan) {
                formData.append('action_plan', responseForm.action_plan);
            }
            
            const result = await apiService.update(APIStudentFeedbackSubmitResponse, formData);
            
            if (result && result.success) {
                showToast("Success", "Response submitted successfully", "success");
                fetchAllData();
                handleCloseResponseDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to submit response", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const getRatingLabel = (rating) => {
        if (rating >= 4.5) return { text: 'Excellent', color: 'success' };
        if (rating >= 3.5) return { text: 'Good', color: 'info' };
        if (rating >= 2.5) return { text: 'Satisfactory', color: 'warning' };
        return { text: 'Needs Improvement', color: 'error' };
    };

    if (loading) {
        return <LoadingState message="Loading Feedback Analytics..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Feedback Analytics" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer>
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <SupervisorBar notificationCount={3} />
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Feedback /> Student Feedback Analytics
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={fetchAllData} 
                                sx={{ 
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Paper>
                </Box>
                {/* Analytics Overview Cards */}
                {analytics && analytics.overall ? (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.info.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {analytics.overall.total_feedback || 0}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Total Feedback
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.success.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {analytics.overall.average_satisfaction || 0}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Avg Satisfaction
                                    </Typography>
                                    <Rating 
                                        value={analytics.overall.average_satisfaction || 0} 
                                        precision={0.1} 
                                        readOnly 
                                        size="small" 
                                        sx={{ mt: 1 }} 
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.warning.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {analytics.response_rate || 0}%
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Response Rate
                                    </Typography>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={analytics.response_rate || 0} 
                                        sx={{ mt: 1, height: 6, borderRadius: 3 }} 
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: (analytics.pending_responses || 0) > 0 ? theme.palette.error.light + '20' : theme.palette.success.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' },
                                        color: (analytics.pending_responses || 0) > 0 ? theme.palette.error.main : 'inherit'
                                    }}>
                                        {analytics.pending_responses || 0}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Pending Responses
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {[1, 2, 3, 4].map((i) => (
                            <Grid item xs={6} sm={3} key={i}>
                                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        <Tab label="Analytics Dashboard" />
                        <Tab label={`All Feedback (${feedbackList.length})`} />
                        <Tab label={`Pending (${feedbackList.filter(f => f.response_required && !f.supervisor_response).length})`} />
                    </Tabs>

                    {/* Analytics Dashboard Tab */}
                    {activeTab === 0 && analytics && analytics.overall && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            {/* Rating Breakdown */}
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                mb: 2,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                Rating Breakdown
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 4 }}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="subtitle2" sx={{ 
                                                fontWeight: 'bold', 
                                                mb: 1,
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}>
                                                Communication
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                <Rating value={analytics.overall.communication || 0} precision={0.1} readOnly />
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {analytics.overall.communication || 0}/5
                                                </Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={((analytics.overall.communication || 0) / 5) * 100} 
                                                sx={{ mt: 1, height: 6, borderRadius: 3 }} 
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="subtitle2" sx={{ 
                                                fontWeight: 'bold', 
                                                mb: 1,
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}>
                                                Guidance Quality
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                <Rating value={analytics.overall.guidance_quality || 0} precision={0.1} readOnly />
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {analytics.overall.guidance_quality || 0}/5
                                                </Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={((analytics.overall.guidance_quality || 0) / 5) * 100} 
                                                sx={{ mt: 1, height: 6, borderRadius: 3 }} 
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="subtitle2" sx={{ 
                                                fontWeight: 'bold', 
                                                mb: 1,
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}>
                                                Availability
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                <Rating value={analytics.overall.availability || 0} precision={0.1} readOnly />
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {analytics.overall.availability || 0}/5
                                                </Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={((analytics.overall.availability || 0) / 5) * 100} 
                                                sx={{ mt: 1, height: 6, borderRadius: 3 }} 
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="subtitle2" sx={{ 
                                                fontWeight: 'bold', 
                                                mb: 1,
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}>
                                                Feedback Timeliness
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                <Rating value={analytics.overall.feedback_timeliness || 0} precision={0.1} readOnly />
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {analytics.overall.feedback_timeliness || 0}/5
                                                </Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={((analytics.overall.feedback_timeliness || 0) / 5) * 100} 
                                                sx={{ mt: 1, height: 6, borderRadius: 3 }} 
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Rating Distribution */}
                            {analytics.rating_distribution && (
                                <>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 'bold', 
                                        mb: 2,
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}>
                                        Satisfaction Distribution
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        {Object.entries(analytics.rating_distribution).map(([rating, count]) => (
                                            <Grid item xs={12} key={rating}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                    <Typography variant="body2" sx={{ 
                                                        width: { xs: '100%', sm: 100 },
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                    }}>
                                                        {rating.replace('_', ' ').toUpperCase()}
                                                    </Typography>
                                                    <LinearProgress 
                                                        variant="determinate" 
                                                        value={(count / (analytics.overall.total_feedback || 1)) * 100} 
                                                        sx={{ flex: 1, height: 8, borderRadius: 3 }} 
                                                    />
                                                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        {count}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </>
                            )}

                            {/* Top Strengths & Improvements */}
                            <Grid container spacing={2} sx={{ mb: 4 }}>
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ 
                                        bgcolor: theme.palette.success.light + '15',
                                        borderLeft: `4px solid ${theme.palette.success.main}`
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h6" sx={{ 
                                                fontWeight: 'bold', 
                                                mb: 2, 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 1,
                                                fontSize: { xs: '1rem', sm: '1.25rem' }
                                            }}>
                                                <ThumbUp color="success" /> Top Strengths
                                            </Typography>
                                            {analytics.top_strengths && Object.keys(analytics.top_strengths).length > 0 ? (
                                                <Stack spacing={1}>
                                                    {Object.entries(analytics.top_strengths).map(([strength, count]) => (
                                                        <Chip 
                                                            key={strength} 
                                                            label={`${strength} (${count})`} 
                                                            color="success" 
                                                            variant="outlined" 
                                                            sx={{ width: '100%', justifyContent: 'flex-start' }} 
                                                            size="small"
                                                        />
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">No strengths data available</Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ 
                                        bgcolor: theme.palette.warning.light + '15',
                                        borderLeft: `4px solid ${theme.palette.warning.main}`
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h6" sx={{ 
                                                fontWeight: 'bold', 
                                                mb: 2, 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 1,
                                                fontSize: { xs: '1rem', sm: '1.25rem' }
                                            }}>
                                                <ThumbDown color="warning" /> Areas for Improvement
                                            </Typography>
                                            {analytics.top_improvements && Object.keys(analytics.top_improvements).length > 0 ? (
                                                <Stack spacing={1}>
                                                    {Object.entries(analytics.top_improvements).map(([improvement, count]) => (
                                                        <Chip 
                                                            key={improvement} 
                                                            label={`${improvement} (${count})`} 
                                                            color="warning" 
                                                            variant="outlined" 
                                                            sx={{ width: '100%', justifyContent: 'flex-start' }} 
                                                            size="small"
                                                        />
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">No improvement areas identified</Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Students Needing Attention */}
                            {analytics.students_needing_attention && analytics.students_needing_attention.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 'bold', 
                                        mb: 2,
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}>
                                        Students Needing Attention
                                    </Typography>
                                    {analytics.students_needing_attention.map((student) => (
                                        <Alert 
                                            key={student.id} 
                                            severity="warning" 
                                            icon={<Person />} 
                                            action={
                                                <Button color="inherit" size="small">
                                                    Reach Out
                                                </Button>
                                            } 
                                            sx={{ mb: 1 }}
                                        >
                                            {student.name} - Last rating: {student.last_rating}/5 ({new Date(student.feedback_date).toLocaleDateString()})
                                        </Alert>
                                    ))}
                                </>
                            )}
                        </Box>
                    )}

                    {/* No Analytics Data Message */}
                    {activeTab === 0 && (!analytics || !analytics.overall) && (
                        <Box sx={{ 
                            p: { xs: 4, sm: 6 }, 
                            textAlign: 'center' 
                        }}>
                            <Analytics sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                No feedback analytics available
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Feedback data will appear once students submit evaluations
                            </Typography>
                        </Box>
                    )}

                    {/* All Feedback Tab */}
                    {activeTab === 1 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            {feedbackList.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
                                    <Feedback sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        No feedback received yet
                                    </Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={2}>
                                    {feedbackList.map((feedback, index) => (
                                        <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={feedback.id}>
                                            <Grid item xs={12}>
                                                <Card sx={{ 
                                                    borderLeft: `4px solid ${theme.palette[feedback.ratings?.average_color || 'grey'].main}`,
                                                    transition: 'transform 0.2s',
                                                    '&:hover': { transform: 'translateX(4px)' }
                                                }}>
                                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            justifyContent: 'space-between', 
                                                            alignItems: 'flex-start', 
                                                            flexWrap: 'wrap', 
                                                            gap: 1, 
                                                            mb: 2,
                                                            flexDirection: { xs: 'column', sm: 'row' }
                                                        }}>
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: 2,
                                                                flexDirection: { xs: 'column', sm: 'row' },
                                                                textAlign: { xs: 'center', sm: 'left' }
                                                            }}>
                                                                <Avatar src={feedback.student?.profile_image}>
                                                                    <Person />
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="subtitle1" sx={{ 
                                                                        fontWeight: 'bold',
                                                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                                                    }}>
                                                                        {feedback.student?.name || 'Unknown'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                        {feedback.formatted_period || 'N/A'} • {feedback.submitted_date ? new Date(feedback.submitted_date).toLocaleDateString() : 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Chip 
                                                                icon={feedback.status_icon} 
                                                                label={feedback.formatted_status || 'Pending'} 
                                                                color={feedback.status_color || 'default'} 
                                                                size="small" 
                                                            />
                                                        </Box>
                                                        
                                                        <Rating value={feedback.ratings?.average || 0} precision={0.1} readOnly size="small" sx={{ mb: 1 }} />
                                                        
                                                        {feedback.comments && (
                                                            <Typography variant="body2" sx={{ 
                                                                mb: 1,
                                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                            }}>
                                                                <strong>Comments:</strong> {feedback.comments}
                                                            </Typography>
                                                        )}
                                                        
                                                        {feedback.areas_for_improvement && (
                                                            <Typography variant="body2" sx={{ 
                                                                mb: 1, 
                                                                color: 'warning.main',
                                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                            }}>
                                                                <strong>Areas for improvement:</strong> {feedback.areas_for_improvement}
                                                            </Typography>
                                                        )}
                                                        
                                                        {feedback.strengths && (
                                                            <Typography variant="body2" sx={{ 
                                                                mb: 1, 
                                                                color: 'success.main',
                                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                            }}>
                                                                <strong>Strengths:</strong> {feedback.strengths}
                                                            </Typography>
                                                        )}
                                                        
                                                        {feedback.supervisor_response && (
                                                            <Alert severity="info" sx={{ mt: 1, p: 1 }}>
                                                                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                    <strong>Your Response:</strong> {feedback.supervisor_response}
                                                                </Typography>
                                                            </Alert>
                                                        )}
                                                        
                                                        {feedback.response_required && !feedback.supervisor_response && (
                                                            <Box sx={{ mt: 2 }}>
                                                                <Button 
                                                                    variant="contained" 
                                                                    size="small" 
                                                                    startIcon={<Send />} 
                                                                    onClick={() => handleOpenResponseDialog(feedback)}
                                                                    fullWidth={isMobile}
                                                                >
                                                                    Respond to Feedback
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Zoom>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    )}

                    {/* Pending Responses Tab */}
                    {activeTab === 2 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            {feedbackList.filter(f => f.response_required && !f.supervisor_response).length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
                                    <CheckCircle sx={{ fontSize: { xs: 48, sm: 60 }, color: 'success.main', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        No pending responses
                                    </Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={2}>
                                    {feedbackList.filter(f => f.response_required && !f.supervisor_response).map((feedback, index) => (
                                        <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={feedback.id}>
                                            <Grid item xs={12}>
                                                <Card sx={{ 
                                                    borderLeft: `4px solid ${theme.palette.warning.main}`,
                                                    transition: 'transform 0.2s',
                                                    '&:hover': { transform: 'translateX(4px)' }
                                                }}>
                                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            justifyContent: 'space-between', 
                                                            alignItems: 'flex-start', 
                                                            flexWrap: 'wrap', 
                                                            gap: 1, 
                                                            mb: 2,
                                                            flexDirection: { xs: 'column', sm: 'row' }
                                                        }}>
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: 2,
                                                                flexDirection: { xs: 'column', sm: 'row' },
                                                                textAlign: { xs: 'center', sm: 'left' }
                                                            }}>
                                                                <Avatar src={feedback.student?.profile_image}>
                                                                    <Person />
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="subtitle1" sx={{ 
                                                                        fontWeight: 'bold',
                                                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                                                    }}>
                                                                        {feedback.student?.name || 'Unknown'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                        {feedback.formatted_period || 'N/A'} • {feedback.submitted_date ? new Date(feedback.submitted_date).toLocaleDateString() : 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Chip 
                                                                icon={<Warning />} 
                                                                label="Response Required" 
                                                                color="warning" 
                                                                size="small" 
                                                            />
                                                        </Box>
                                                        
                                                        <Rating value={feedback.ratings?.average || 0} precision={0.1} readOnly size="small" sx={{ mb: 1 }} />
                                                        
                                                        {feedback.comments && (
                                                            <Typography variant="body2" sx={{ 
                                                                mb: 1,
                                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                            }}>
                                                                <strong>Comments:</strong> {feedback.comments}
                                                            </Typography>
                                                        )}
                                                        
                                                        {feedback.areas_for_improvement && (
                                                            <Typography variant="body2" sx={{ 
                                                                mb: 1, 
                                                                color: 'warning.main',
                                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                            }}>
                                                                <strong>Areas for improvement:</strong> {feedback.areas_for_improvement}
                                                            </Typography>
                                                        )}
                                                        
                                                        <Box sx={{ mt: 2 }}>
                                                            <Button 
                                                                variant="contained" 
                                                                startIcon={<Send />} 
                                                                onClick={() => handleOpenResponseDialog(feedback)}
                                                                fullWidth={isMobile}
                                                            >
                                                                Respond to Feedback
                                                            </Button>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Zoom>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    )}
                </Paper>

                {/* Response Dialog */}
                <Dialog
                    open={openResponseDialog}
                    onClose={handleCloseResponseDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Respond to Student Feedback
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            {selectedFeedback && (
                                <>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            <strong>Student:</strong> {selectedFeedback.student?.name || 'Unknown'}<br />
                                            <strong>Rating:</strong> {selectedFeedback.ratings?.average || 0}/5<br />
                                            {selectedFeedback.comments && <><strong>Feedback:</strong> {selectedFeedback.comments}</>}
                                        </Typography>
                                    </Alert>
                                    
                                    <TextField
                                        fullWidth
                                        label="Your Response *"
                                        multiline
                                        rows={4}
                                        sx={{ mb: 2 }}
                                        value={responseForm.response}
                                        onChange={(e) => setResponseForm({ ...responseForm, response: e.target.value })}
                                        placeholder="Thank the student for their feedback and address any concerns..."
                                        size={isMobile ? "small" : "medium"}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        label="Action Plan (Optional)"
                                        multiline
                                        rows={3}
                                        value={responseForm.action_plan}
                                        onChange={(e) => setResponseForm({ ...responseForm, action_plan: e.target.value })}
                                        placeholder="What actions will you take based on this feedback?"
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseResponseDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSubmitResponse} 
                            variant="contained" 
                            disabled={actionLoading || !responseForm.response}
                            size={isMobile ? "small" : "medium"}
                        >
                            {actionLoading ? <CircularProgress size={24} /> : 'Submit Response'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default StudentFeedbackAnalytics;